import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, switchMap } from 'rxjs/operators';
import { from, Observable, throwError } from 'rxjs';
import { AuthOrchestratorService } from '../auth/auth-orchestrator.service';
import { AuthService } from '../auth/auth.service';

export const tokenInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn,
): Observable<HttpEvent<any>> => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const authOrchestrator = inject(AuthOrchestratorService);

  return authService.getAccessToken().pipe(
    switchMap(([principalToken, oasToken, msToken]) => {
      let headers = req.headers;

      // Add principal token to header
      if (principalToken) {
        headers = headers.set('Authorization', `Bearer ${principalToken}`);
      }

      // add OAS token to header
      if (oasToken) {
        headers = headers.set('XoasAuthServerToken', oasToken);
      }

      // Add MS token to header
      if (msToken) {
        headers = headers.set('XmsAuthServerToken', msToken);
      }

      const clonedReq = req.clone({ headers });

      return next(clonedReq).pipe(
        catchError((error: HttpErrorResponse) => {
          if (
            error.status === HttpStatusCode.Unauthorized &&
            authService.isAnyAccessTokenExpired([principalToken, oasToken, msToken])
          ) {
            // if any token is expired, send the request again - getAccessToken will generate new token
            return next(clonedReq);
          }

          // handle error
          return handleHttpError(error, authService, router, authOrchestrator);
        }),
      );
    }),
  );
};

function handleHttpError(
  error: HttpErrorResponse,
  authService: AuthService,
  router: Router,
  authOrchestrator: AuthOrchestratorService,
): Observable<any> {
  return from(authService.isAuthenticated()).pipe(
    switchMap((isAuthenticated) => {
      // if unauthorized and not all providers are authenticated, restart the workflow
      if (error.status === HttpStatusCode.Unauthorized && !isAuthenticated) {
        authOrchestrator.restartWorkflow().then();
      }

      if ([HttpStatusCode.Unauthorized, HttpStatusCode.Forbidden].includes(error.status)) {
        router.navigate(['/access-error'], { queryParams: { message: 'interceptor' } }).then();
      }
      return throwError(() => error);
    }),
  );
}
